import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  Briefcase,
  FileText,
  Shield,
  DollarSign,
  CheckCircle,
  Clock,
  User,
} from 'lucide-react';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useNavigation } from '../../contexts/NavigationContext';

const formatMoney = (n: number | undefined | null) =>
  n != null ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : '-';

interface EscrowAgreement {
  _id: string;
  status: string;
  total_amount: number;
  currency: string;
  schedule_type?: string;
  payer_org_id?: { companyName: string };
  payee_org_id?: { companyName: string };
  milestones?: EscrowMilestone[];
}

interface EscrowMilestone {
  _id: string;
  title: string;
  amount: number;
  percent?: number;
  status: string;
  due_date?: string;
}

interface Project {
  _id: string;
  project_ref: string;
  project_name: string;
  country_code: string;
  contract_value: number;
  client_org_id?: { companyName: string };
  client_contact_id?: { name: string };
  main_contractor_org_id?: { companyName: string };
}

interface ClientConstructionProjectViewProps {
  projectId: string;
  onBack: () => void;
}

export const ClientConstructionProjectView: React.FC<ClientConstructionProjectViewProps> = ({
  projectId,
  onBack,
}) => {
  const { setCustomBreadcrumbs } = useBreadcrumbs();
  const { t } = useTranslation();
  const [project, setProject] = useState<Project | null>(null);
  const [escrowAgreements, setEscrowAgreements] = useState<EscrowAgreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError(null);
      const [projectRes, agreementsRes] = await Promise.all([
        fetch(`${apiUrl}/api/construction/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${apiUrl}/api/construction/projects/${projectId}/escrow-agreements`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const projectData = await projectRes.json();
      const agreementsData = await agreementsRes.json();

      if (projectData.success && projectData.data) {
        setProject(projectData.data);
      } else {
        setError(projectData.message || 'Failed to load project');
      }

      if (agreementsData.success && agreementsData.data) {
        const agreements = agreementsData.data;
        for (const a of agreements) {
          const detailRes = await fetch(`${apiUrl}/api/construction/escrow-agreements/${a._id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const detailData = await detailRes.json();
          if (detailData.success && detailData.data?.milestones) {
            a.milestones = detailData.data.milestones;
          }
        }
        setEscrowAgreements(agreements);
      }
    } catch (err) {
      setError('Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, projectId, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setCustomBreadcrumbs([
      { label: t('navigation.home') || 'Home', path: '/', icon: 'Home' },
      { label: t('navigation.projects') || 'Projects', path: '/projects', icon: 'Briefcase' },
      { label: project?.project_ref || projectId, path: `/projects/${projectId}`, icon: 'FileText' },
      { label: 'Client View', path: `/projects/${projectId}/client-view`, icon: 'User' },
    ]);
    return () => setCustomBreadcrumbs(null);
  }, [t, setCustomBreadcrumbs, projectId, project?.project_ref]);

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      awaiting_funding: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
      funded: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      in_progress: 'bg-accent-100 text-accent-800 dark:bg-accent-900/30 dark:text-accent-400',
      awaiting_approval: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      released: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    };
    return (
      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-100'}`}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  const milestoneStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      funded: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      submitted: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
      approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      released: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    };
    return (
      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-100'}`}>
        {status}
      </span>
    );
  };

  if (loading || !project) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg">
          {error}
        </div>
        <button
          onClick={onBack}
          className="mt-4 inline-flex items-center text-accent-600 hover:text-accent-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Projects
        </button>
        <span className="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded">
          Client view
        </span>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-l-4 border-accent-500">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-accent-100 dark:bg-accent-900/30 rounded-lg">
            <User className="w-8 h-8 text-accent-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {project.project_ref} – {project.project_name}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              This is the client-facing view of your escrow project.
            </p>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
              <span>{project.country_code}</span>
              <span>Value: {formatMoney(project.contract_value)}</span>
              {project.main_contractor_org_id && (
                <span>Contractor: {project.main_contractor_org_id.companyName}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {escrowAgreements.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No escrow agreements yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Escrow agreements will appear here once they are created for this project.
          </p>
        </div>
      ) : (
        escrowAgreements.map((agreement) => (
          <div key={agreement._id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Shield className="w-5 h-5 text-accent-600" />
                Escrow Agreement
              </h2>
              <div className="flex items-center gap-2">
                {statusBadge(agreement.status)}
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {formatMoney(agreement.total_amount)} {agreement.currency}
                </span>
              </div>
            </div>
            <div className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
              {(agreement.payer_org_id as any)?.companyName} → {(agreement.payee_org_id as any)?.companyName}
            </div>
            {agreement.milestones && agreement.milestones.length > 0 && (
              <div className="border-t border-gray-200 dark:border-gray-700">
                <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700/50 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Milestones
                </div>
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {agreement.milestones.map((m) => (
                    <li key={m._id} className="px-6 py-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {m.status === 'released' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <Clock className="w-5 h-5 text-gray-400" />
                        )}
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{m.title}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {formatMoney(m.amount)}
                            {m.percent != null && ` (${m.percent}%)`}
                          </div>
                        </div>
                      </div>
                      {milestoneStatusBadge(m.status)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))
      )}

      <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">
        Clients can sign agreements, deposit funds, and approve milestones from this view.
      </div>
    </div>
  );
};
